import type { NewRecipe, Recipe } from '../domain/types.js';

export const DUPLICATE_SIMILARITY_THRESHOLD = 0.9;

export interface RecipeMatch {
  readonly recipeId: string;
  readonly score: number;
}

/** Lowercase, strip punctuation, collapse whitespace. */
export function normalizeForCompare(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokens(value: string): string[] {
  const normalized = normalizeForCompare(value);
  return normalized === '' ? [] : normalized.split(' ');
}

function bigrams(value: string): string[] {
  const normalized = normalizeForCompare(value).replace(/\s+/g, '');
  if (normalized.length < 2) return normalized === '' ? [] : [normalized];
  const out: string[] = [];
  for (let i = 0; i < normalized.length - 1; i += 1) {
    out.push(normalized.slice(i, i + 2));
  }
  return out;
}

/** Sørensen–Dice coefficient over multisets. */
function dice(a: readonly string[], b: readonly string[]): number {
  if (a.length === 0 && b.length === 0) return 1;
  if (a.length === 0 || b.length === 0) return 0;

  const counts = new Map<string, number>();
  for (const item of a) counts.set(item, (counts.get(item) ?? 0) + 1);

  let overlap = 0;
  for (const item of b) {
    const remaining = counts.get(item) ?? 0;
    if (remaining > 0) {
      overlap += 1;
      counts.set(item, remaining - 1);
    }
  }

  return (2 * overlap) / (a.length + b.length);
}

function jaccard(a: readonly string[], b: readonly string[]): number {
  if (a.length === 0 && b.length === 0) return 1;
  if (a.length === 0 || b.length === 0) return 0;
  const left = new Set(a);
  const right = new Set(b);
  let intersection = 0;
  for (const item of left) {
    if (right.has(item)) intersection += 1;
  }
  return intersection / (left.size + right.size - intersection);
}

function titleScore(left: string, right: string): number {
  return dice(bigrams(left), bigrams(right));
}

function ingredientScore(
  left: readonly { name: string }[],
  right: readonly { name: string }[],
): number {
  const leftNames = left.map((item) => normalizeForCompare(item.name)).filter((name) => name !== '');
  const rightNames = right.map((item) => normalizeForCompare(item.name)).filter((name) => name !== '');
  return jaccard(leftNames, rightNames);
}

function instructionScore(
  left: readonly { text: string }[],
  right: readonly { text: string }[],
): number {
  return dice(
    tokens(left.map((step) => step.text).join(' ')),
    tokens(right.map((step) => step.text).join(' ')),
  );
}

/**
 * Weighted similarity between a draft import and an existing recipe.
 * Title 40%, ingredients 40%, instructions 20%. Empty sides redistribute weight.
 */
export function recipeSimilarity(draft: NewRecipe, existing: Recipe): number {
  const title = titleScore(draft.title, existing.title);
  const ingredients = ingredientScore(draft.ingredients ?? [], existing.ingredients);
  const instructions = instructionScore(draft.instructions ?? [], existing.instructions);

  const draftHasIngredients = (draft.ingredients?.length ?? 0) > 0;
  const existingHasIngredients = existing.ingredients.length > 0;
  const draftHasInstructions = (draft.instructions?.length ?? 0) > 0;
  const existingHasInstructions = existing.instructions.length > 0;

  let titleWeight = 0.4;
  let ingredientWeight = draftHasIngredients || existingHasIngredients ? 0.4 : 0;
  let instructionWeight = draftHasInstructions || existingHasInstructions ? 0.2 : 0;
  const used = titleWeight + ingredientWeight + instructionWeight;
  if (used < 1) {
    const scale = 1 / used;
    titleWeight *= scale;
    ingredientWeight *= scale;
    instructionWeight *= scale;
  }

  return title * titleWeight + ingredients * ingredientWeight + instructions * instructionWeight;
}

export interface FindDuplicateOptions {
  readonly threshold?: number;
  /** Recipe ids that must not count as duplicates (this import's own recipe). */
  readonly excludeIds?: Iterable<string>;
}

/** Best match at or above the duplicate threshold, or null if none. */
export function findDuplicateRecipe(
  draft: NewRecipe,
  recipes: readonly Recipe[],
  options: FindDuplicateOptions = {},
): RecipeMatch | null {
  const threshold = options.threshold ?? DUPLICATE_SIMILARITY_THRESHOLD;
  const excludeIds = options.excludeIds == null ? null : new Set(options.excludeIds);
  let best: RecipeMatch | null = null;
  for (const recipe of recipes) {
    if (recipe.deletedAt != null) continue;
    if (excludeIds?.has(recipe.id)) continue;
    const score = recipeSimilarity(draft, recipe);
    if (score < threshold) continue;
    if (best == null || score > best.score) {
      best = { recipeId: recipe.id, score };
    }
  }
  return best;
}

export type RecipeImportPlan =
  | { readonly kind: 'already-imported'; readonly recipeId: string }
  | { readonly kind: 'duplicate'; readonly recipeId: string; readonly score: number }
  | { readonly kind: 'create'; readonly recipeId: string };

/**
 * Decide how to commit a draft against the current cookbook.
 *
 * `claimedId` is this import file's recipe id, persisted before addRecipe so a retry
 * can recognise the row it already wrote instead of treating it as a duplicate.
 */
export function planRecipeImport(
  draft: NewRecipe,
  recipes: readonly Recipe[],
  options: { readonly claimedId: string; readonly force?: boolean },
): RecipeImportPlan {
  const { claimedId, force = false } = options;
  if (!force) {
    const existing = recipes.find((recipe) => recipe.id === claimedId && recipe.deletedAt == null);
    if (existing != null) {
      return { kind: 'already-imported', recipeId: claimedId };
    }
    const duplicate = findDuplicateRecipe(draft, recipes, { excludeIds: [claimedId] });
    if (duplicate != null) {
      return { kind: 'duplicate', recipeId: duplicate.recipeId, score: duplicate.score };
    }
  }
  return { kind: 'create', recipeId: claimedId };
}
